import React, { useCallback, useEffect, useRef, Children } from 'react';
import Select, { components } from 'react-select';

const blockReactSelectPointer = (e) => {
  if (e.button !== 0) return;
  e.preventDefault();
  e.stopPropagation();
};

const MultiLikertControl = (props) => (
  <components.Control
    {...props}
    innerProps={{
      ...props.innerProps,
      onMouseDown: blockReactSelectPointer,
      onTouchEnd: blockReactSelectPointer,
    }}
  />
);

const MultiLikertDropdownIndicator = (props) => (
  <components.DropdownIndicator
    {...props}
    innerProps={{
      ...props.innerProps,
      onMouseDown: blockReactSelectPointer,
      onTouchEnd: blockReactSelectPointer,
    }}
  />
);

let activeLikertMenuKey = null;

const HEADER_OFFSET = 100;
const scrollOptionBarToTop = (element) => {
  if (!element) return;
  const elementPosition = element.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.pageYOffset - HEADER_OFFSET;
  window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
};

const generateGradientColors = steps => {
  const startColor = [209, 70, 27];
  const middleColor = [128, 128, 128];
  const endColor = [36, 98, 162];

  return Array.from({ length: steps }, (_, i) => {
    let r, g, b;

    if (i < Math.floor(steps / 2)) {
      r = Math.round(startColor[0] + ((middleColor[0] - startColor[0]) * i) / (Math.floor(steps / 2) - 1));
      g = Math.round(startColor[1] + ((middleColor[1] - startColor[1]) * i) / (Math.floor(steps / 2) - 1));
      b = Math.round(startColor[2] + ((middleColor[2] - startColor[2]) * i) / (Math.floor(steps / 2) - 1));
    } else {
      const stepIndex = i - Math.floor(steps / 2);
      r = Math.round(middleColor[0] + ((endColor[0] - middleColor[0]) * stepIndex) / (Math.ceil(steps / 2) - 1));
      g = Math.round(middleColor[1] + ((endColor[1] - middleColor[1]) * stepIndex) / (Math.ceil(steps / 2) - 1));
      b = Math.round(middleColor[2] + ((endColor[2] - middleColor[2]) * stepIndex) / (Math.ceil(steps / 2) - 1));
    }

    return `rgb(${r},${g},${b})`;
  });
};

const toRgb = ([r, g, b]) => `rgb(${r},${g},${b})`;

const LIKERT_SCALE_COLORS = {
  5: [
    [209, 70, 27],
    [217, 111, 79],
    [166, 166, 166],
    [93, 130, 159],
    [36, 98, 162],
  ].map(toRgb),
  7: [
    [209, 70, 27],
    [214, 95, 61],
    [220, 137, 110],
    [166, 166, 166],
    [113, 147, 172],
    [78, 126, 160],
    [36, 98, 162],
  ].map(toRgb),
  10: [
    [209, 70, 27],
    [214, 91, 57],
    [219, 118, 88],
    [224, 146, 119],
    [208, 155, 136],
    [162, 143, 138],
    [125, 145, 160],
    [93, 130, 159],
    [65, 114, 160],
    [36, 98, 162],
  ].map(toRgb),
};

const getLikertScaleColors = steps => LIKERT_SCALE_COLORS[steps] || generateGradientColors(steps);

const CUSTOM_SELECTION_GREEN = '#9ebe21';

const normalizedLikertScaleType = question => {
  const scaleType = question?.LikertScaleType || question?.scaleType;
  if (typeof scaleType !== 'string') return '';
  return scaleType.trim().toLowerCase();
};

export const isCustomLikertQuestion = question => {
  const normalized = normalizedLikertScaleType(question);
  return normalized === 'custom likert' || normalized === 'custom';
};

export const isMatrixQuestion = question => normalizedLikertScaleType(question) === 'matrix';

const sameScaleId = (a, b) => a === b || String(a) === String(b);

const LikertDropdown = ({
  choice,
  question,
  selectedAnswer,
  compositeKey,
  onAnswerChange,
  disabled = false,
  menuIsOpen,
  onMenuClose,
  onControlledMenuOpen
}) => {
  const isMatrix = isMatrixQuestion(question);
  const isCustomScale = question.LikertScaleType === 'custom' || question.LikertScaleType === 'Custom Likert' || isMatrix;
  const isMultiSelect = isCustomLikertQuestion(question) || isMatrix;
  const gradientColors = getLikertScaleColors(question.likertScaleName_array.length);

  const likertOptions = question.likertScaleName_array.map((scale, index) => ({
    value: scale.questionLikertScale_ID,
    label: scale.questionLikertScaleName,
    color: isCustomScale ? '#ccc' : gradientColors[index],
    selectionColor: isCustomScale ? CUSTOM_SELECTION_GREEN : gradientColors[index],
    index: index
  }));

  const selectedValues = Array.isArray(selectedAnswer)
    ? selectedAnswer
    : selectedAnswer !== undefined && selectedAnswer !== null && selectedAnswer !== ''
      ? [selectedAnswer]
      : [];

  const selectedOptions = selectedValues
    .map(id => likertOptions.find(opt => sameScaleId(opt.value, id)))
    .filter(Boolean);

  const selectedOption = selectedOptions[0];

  const optionBarRef = useRef(null);
  const selectRef = useRef(null);
  const focusFrameRef = useRef(null);
  const isMultiControlled = Boolean(onControlledMenuOpen);
  const isMenuOpen = isMultiControlled && menuIsOpen === true;

  useEffect(() => {
    if (isMenuOpen) {
      activeLikertMenuKey = compositeKey;
      const scrollTimer = window.setTimeout(() => {
        scrollOptionBarToTop(optionBarRef.current);
      }, 0);
      focusFrameRef.current = requestAnimationFrame(() => {
        selectRef.current?.focus();
      });
      return () => {
        clearTimeout(scrollTimer);
        if (focusFrameRef.current !== null) {
          cancelAnimationFrame(focusFrameRef.current);
          focusFrameRef.current = null;
        }
      };
    }

    if (activeLikertMenuKey === compositeKey) {
      activeLikertMenuKey = null;
    }
  }, [isMenuOpen, isMultiControlled, compositeKey]);

  const requestOpen = useCallback(() => {
    activeLikertMenuKey = compositeKey;
    scrollOptionBarToTop(optionBarRef.current);
    onControlledMenuOpen?.();
  }, [compositeKey, onControlledMenuOpen]);

  const requestClose = useCallback(() => {
    if (activeLikertMenuKey && activeLikertMenuKey !== compositeKey) return;
    activeLikertMenuKey = null;
    onMenuClose?.();
  }, [compositeKey, onMenuClose]);

  const toggleControlledMenu = useCallback(() => {
    if (isMenuOpen) {
      requestClose();
      return;
    }
    requestOpen();
  }, [isMenuOpen, requestClose, requestOpen]);

  const handleMenuOpen = useCallback(() => {
    scrollOptionBarToTop(optionBarRef.current);
  }, []);

  const handleBoxPointerDown = useCallback((e) => {
    if (disabled || e.button !== 0) return;
    if (e.target.closest('.likert-select__menu')) return;
    if (!e.target.closest('.likert-select__control')) return;

    e.preventDefault();
    e.stopPropagation();

    if (isMultiControlled) {
      toggleControlledMenu();
      return;
    }

    selectRef.current?.focus();
  }, [disabled, isMultiControlled, toggleControlledMenu]);

  const formatOptionLabel = (option, { context }) => {
    const isSelectedInMenu = context === 'menu' && selectedOptions.some(opt => sameScaleId(opt.value, option.value));
    const borderColor = isCustomScale ? option.color : '#fff';
    const fillColor = isCustomScale
      ? (isSelectedInMenu ? option.selectionColor : 'transparent')
      : option.selectionColor;

    if (context === 'value') {
      return <span>{option.label}</span>;
    }

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <div style={{
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          border: `2px solid ${borderColor}`,
          backgroundColor: fillColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          {isSelectedInMenu && (
            <div style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              backgroundColor: '#fff'
            }} />
          )}
        </div>
        <span style={{
          color: isCustomScale ? '#000' : '#fff',
          fontWeight: isSelectedInMenu ? '600' : 'normal',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          lineHeight: '1.4'
        }}>
          {option.label}
        </span>
      </div>
    );
  };

  const isSelected = selectedOptions.length > 0;

  const likertSelectStyles = {
    control: (base) => ({
      ...base,
      border: isSelected ? '2px solid #9ebe21' : '1px solid #ccc',
      borderRadius: '6px',
      boxShadow: 'none',
      fontSize: '16px',
      backgroundColor: isSelected ? '#fff' : 'rgb(239 239 239)',
      color: '#000',
      minHeight: 'auto',
      height: 'auto',
      padding: '0',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      display: 'flex',
      flexWrap: 'nowrap',
      alignItems: 'stretch',
      transition: 'background-color 0.2s ease, border 0.2s ease',
      '&:hover': {
        borderColor: '#9ebe21',
      }
    }),
    singleValue: (base) => ({
      ...base,
      display: 'none'
    }),
    placeholder: (base) => ({
      ...base,
      color: '#888',
      margin: '0',
      padding: '0'
    }),
    valueContainer: (base) => ({
      ...base,
      display: 'flex',
      flex: '1 1 auto',
      flexWrap: 'nowrap',
      overflow: 'visible',
      padding: '0',
      margin: '0',
      minWidth: 0,
      alignItems: 'center'
    }),
    container: (base) => ({
      ...base,
      overflow: 'visible'
    }),
    menu: (base) => (
      isMatrix
        ? {
            ...base,
            position: 'relative',
            top: 'auto',
            bottom: 'auto',
            zIndex: 1,
            marginTop: 8,
            marginBottom: 4,
            overflow: 'visible',
            boxShadow: 'none'
          }
        : {
            ...base,
            zIndex: 100,
            marginTop: 2,
            overflow: 'visible'
          }
    ),
    option: (base, { isSelected, data }) => {
      const optionBorderColor = data.color;
      const optionSelectionColor = data.selectionColor ?? data.color;
      return {
        ...base,
        backgroundColor: isCustomScale
          ? (isSelected ? '#f0f8e8' : '#fff')
          : optionSelectionColor,
        color: isCustomScale ? '#000' : '#fff',
        cursor: 'pointer',
        fontSize: '15px',
        padding: '12px 16px',
        border: `1px solid ${isSelected ? optionSelectionColor : optionBorderColor}`,
        borderRadius: '6px',
        margin: '4px 8px 4px 3px',
        '&:hover': {
          borderColor: optionSelectionColor,
          backgroundColor: isCustomScale ? '#f9f9f9' : optionSelectionColor
        },
        '&:active': {
          backgroundColor: isCustomScale ? '#f0f0f0' : optionSelectionColor
        }
      };
    },
    menuList: (base) => ({
      ...base,
      padding: '8px 8px 8px 0',
      maxHeight: 'none',
      overflow: 'visible'
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    indicatorsContainer: (base) => ({
      ...base,
      display: 'flex',
      alignItems: 'center',
      alignSelf: 'center',
      flexShrink: 0,
      flexGrow: 0,
      height: 'auto',
      width: 'auto',
      paddingRight: '12px'
    }),
    dropdownIndicator: base => ({
      ...base,
      color: '#888',
      padding: '0 8px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 'auto',
      height: 'auto',
      flexShrink: 0
    }),
  };

  return (
    <div ref={optionBarRef} style={{ marginBottom: '0.75rem', marginTop: '0.75rem' }}>
      <div
        style={{
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? 'none' : 'auto'
        }}
        onMouseDownCapture={handleBoxPointerDown}
      >
        <Select
          ref={selectRef}
          classNamePrefix="likert-select"
          key={`likert-${compositeKey}`}
          isMulti={isMultiSelect}
          isClearable={false}
          hideSelectedOptions={false}
          controlShouldRenderValue={false}
          value={isMultiSelect ? selectedOptions : (selectedOption || null)}
          onChange={(selected) => {
            if (disabled) return;
            if (isMultiSelect) {
              const values = Array.isArray(selected) ? selected.map(opt => opt.value) : [];
              onAnswerChange(compositeKey, values.length ? values : null);
              return;
            }
            if (selected && sameScaleId(selected.value, selectedAnswer)) {
              onAnswerChange(compositeKey, null);
              return;
            }
            if (selected) {
              onAnswerChange(compositeKey, selected.value);
            }
          }}
          options={likertOptions}
          placeholder={choice.choiceName}
          isDisabled={disabled}
          styles={likertSelectStyles}
          isSearchable={false}
          formatOptionLabel={formatOptionLabel}
          {...(isMatrix ? { menuPlacement: 'bottom' } : {})}
          closeMenuOnSelect={isMultiSelect ? false : !isMultiControlled}
          blurInputOnSelect={isMultiSelect ? false : !isMultiControlled}
          openMenuOnFocus={!isMultiControlled}
          openMenuOnClick={!isMultiControlled}
          maxMenuHeight={10000}
          onMenuOpen={isMultiControlled ? undefined : handleMenuOpen}
          {...(isMultiControlled ? {
            menuIsOpen: isMenuOpen,
            onMenuClose: requestClose,
          } : {})}
          components={{
            ...(isMultiControlled ? {
              Control: MultiLikertControl,
              DropdownIndicator: MultiLikertDropdownIndicator,
            } : {}),
            SingleValue: () => null,
            MultiValue: () => null,
            MultiValueRemove: () => null,
            ClearIndicator: () => null,
            ValueContainer: (containerProps) => {
              const { selectProps, innerProps, children } = containerProps;

              return (
                <div
                  {...innerProps}
                  style={{
                    ...(innerProps?.style || {}),
                    display: 'flex',
                    flex: '1 1 auto',
                    minWidth: 0,
                    boxSizing: 'border-box',
                    padding: '12px 8px 12px 16px',
                    alignItems: 'center',
                    position: 'relative',
                  }}
                >
                  <span style={{
                    color: '#322a2a',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: '1.5',
                    width: '100%',
                    display: 'block'
                  }}>
                    {selectProps.placeholder}
                  </span>
                  {Children.toArray(children).filter(
                    (child) =>
                      child?.props?.readOnly === true ||
                      child?.props?.isHidden === true
                  )}
                </div>
              );
            }
          }}
        />
      </div>
    </div>
  );
};

export default LikertDropdown;